import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Logo2 } from "../static";
import { api } from "../axios/api";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loginUser } from '../redux/userSlice';
import { 
  Building2, Globe, ChevronRight, ChevronLeft, 
  LogOut, Shield, CheckCircle2, Info, UserCheck
} from "lucide-react";

const Cadastro = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logotipo, setLogotipo] = useState(null);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [regimes, setRegimes] = useState([]);

  // Removido o campo nome_comercial e private_key do estado
  const [formData, setFormData] = useState({
    nome: "", nif: "", email: "",
    contacto: "", contacto_alternativo: "", endereco: "",
    pais_id: "", provincia_id: "", regime_iva_id: "",
    admin_nome: "", admin_email: "", 
    admin_telefone: "", admin_senha: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resPaises, resRegimes] = await Promise.all([
          api.get("/config/paises/"),
          api.get("/config/regimes-iva/")
        ]);
        setPaises(resPaises.data.map((p) => ({ value: p.id, label: p.nome })));
        setRegimes(resRegimes.data.map((r) => ({ value: r.id, label: r.regime })));
      } catch (err) { console.error("Erro ao carregar dados:", err); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!formData.pais_id) { setProvincias([]); return; }
    const fetchProvincias = async () => {
      try {
        const res = await api.get(`/config/provincias/pais/${formData.pais_id}/`);
        setProvincias(res.data.map((prov) => ({ value: prov.id, label: prov.nome })));
      } catch (err) { console.error("Erro ao carregar províncias:", err); }
    };
    fetchProvincias();
  }, [formData.pais_id]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleFileChange = (e) => setLogotipo(e.target.files[0]);
  const handleSelectChange = (field, option) => {
    setFormData({ ...formData, [field]: option ? option.value : "", ...(field === "pais_id" && { provincia_id: "" }) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) { setStep(step + 1); return; }

    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
          data.append(key, formData[key]);
      });
      if (logotipo) data.append("file", logotipo);

      await api.post("/auth/empresas/setup/", data);
      await Swal.fire({ title: "Sucesso", icon: "success", confirmButtonColor: '#0078d4' });
      await dispatch(loginUser({ email: formData.admin_email, senha: formData.admin_senha })).unwrap();
      navigate("/dashboard");
    } catch (err) {
      Swal.fire("Erro", "Falha no registo", "error");
    } finally { setLoading(false); }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '12px',
      border: state.isFocused ? '1.5px solid #0078d4' : '1.5px solid #e2e8f0',
      backgroundColor: '#f8fafc',
      padding: '4px',
      textAlign: 'left'
    })
  };

  return (
    <div className="facturize-auth-page min-vh-100 d-flex align-items-center justify-content-center">
      <div className="vignette" />
      
      <div className="container py-4 position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11 col-xl-10">
            <div className="facturize-setup-card animate__animated animate__fadeIn">
              <div className="row g-0">
                
                {/* Sidebar */}
                <div className="col-lg-4 auth-sidebar p-4 d-none d-lg-flex flex-column justify-content-between">
                  <div>
                    <div className="mb-5 text-center text-lg-start">
                      <img src={Logo2} width="45" alt="Logo" className="mb-3" />
                      <h4 className="sidebar-title">Configurações</h4>
                      <p className="sidebar-desc">Configuração inicial do sistema Facturize.</p>
                    </div>
                    <div className="step-list">
                      {[
                        { s: 1, t: "Empresa", i: <Building2 size={20}/> },
                        { s: 2, t: "Regional", i: <Globe size={20}/> },
                        { s: 3, t: "Acesso Admin", i: <Shield size={20}/> }
                      ].map((item) => (
                        <div key={item.s} className={`step-item ${step === item.s ? 'active' : ''} ${step > item.s ? 'completed' : ''}`}>
                          <div className="step-icon">{step > item.s ? <CheckCircle2 size={18} /> : item.i}</div>
                          <div className="step-text">
                            <span className="step-number">0{item.s}</span>
                            <span className="step-label">{item.t}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Link to="/login" className="exit-button">
                    <LogOut size={18} className="me-2"/> Voltar ao Login
                  </Link>
                </div>

                {/* Form Content */}
                <div className="col-lg-8 bg-white p-4 p-md-5">
                  <div className="form-header mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="step-title">Passo {step} de 3</h2>
                        <span className="setup-badge">CADASTRO DE EMPRESA</span>
                    </div>
                    <div className="setup-progress-container">
                        <div className="progress-fill" style={{ width: `${(step/3)*100}%` }}></div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {step === 1 && (
                      <div className="row g-3">
                        <div className="col-md-12 mb-2"><h5 className="section-title"><Info size={18} className="me-2" /> Identificação da Empresa</h5></div>
                        <div className="col-md-12"><label className="auth-label">Nome Oficial *</label><input type="text" className="auth-field" name="nome" value={formData.nome} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">NIF *</label><input type="text" className="auth-field" name="nif" value={formData.nif} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Email Institucional *</label><input type="email" className="auth-field" name="email" value={formData.email} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Telefone Principal *</label><input type="text" className="auth-field" name="contacto" value={formData.contacto} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Telefone Alternativo</label><input type="text" className="auth-field" name="contacto_alternativo" value={formData.contacto_alternativo} onChange={handleChange} /></div>
                        <div className="col-12"><label className="auth-label">País *</label><Select styles={selectStyles} options={paises} value={paises.find(p => p.value === formData.pais_id)} onChange={opt => handleSelectChange("pais_id", opt)} /></div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="row g-3">
                        <div className="col-md-12 mb-2"><h5 className="section-title"><Globe size={18} className="me-2" /> Localização e Impostos</h5></div>
                        <div className="col-md-6"><label className="auth-label">Província *</label><Select styles={selectStyles} options={provincias} value={provincias.find(p => p.value === formData.provincia_id)} onChange={opt => handleSelectChange("provincia_id", opt)} /></div>
                        <div className="col-md-6"><label className="auth-label">Regime de IVA *</label><Select styles={selectStyles} options={regimes} value={regimes.find(r => r.value === formData.regime_iva_id)} onChange={opt => handleSelectChange("regime_iva_id", opt)} /></div>
                        <div className="col-12"><label className="auth-label">Endereço Sede *</label><input type="text" className="auth-field" name="endereco" value={formData.endereco} onChange={handleChange} required /></div>
                        <div className="col-12">
                           <label className="auth-label">Logotipo da Instituição</label>
                           <input type="file" className="auth-field" onChange={handleFileChange} accept="image/*" />
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="row g-3">
                        <div className="col-md-12 mb-2"><h5 className="section-title"><UserCheck size={18} className="me-2" /> Conta do Administrador</h5></div>
                        <div className="col-md-6"><label className="auth-label">Nome do Administrador *</label><input type="text" className="auth-field" name="admin_nome" value={formData.admin_nome} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Telefone do Administrador *</label><input type="text" className="auth-field" name="admin_telefone" value={formData.admin_telefone} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Email de Acesso *</label><input type="email" className="auth-field" name="admin_email" value={formData.admin_email} onChange={handleChange} required /></div>
                        <div className="col-md-6"><label className="auth-label">Senha de Acesso *</label><input type="password" className="auth-field" name="admin_senha" value={formData.admin_senha} onChange={handleChange} required /></div>
                      </div>
                    )}

                    <div className="form-footer mt-5 d-flex justify-content-between">
                      {step > 1 ? (
                        <button type="button" onClick={() => setStep(step - 1)} className="btn-setup-secondary">
                          <ChevronLeft size={18}/> Anterior
                        </button>
                      ) : <div/>}
                      <button type="submit" disabled={loading} className={step < 3 ? "btn-setup-primary" : "btn-setup-finish"}>
                        {loading ? "Processando..." : step < 3 ? "Próximo Passo" : "Finalizar Configuração"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .facturize-auth-page { background: #000b1a; font-family: 'Inter', sans-serif; position: relative; }
        .vignette { position: absolute; width: 100%; height: 100%; background: radial-gradient(circle at center, rgba(0, 86, 179, 0.15) 0%, #000b1a 100%); }
        .facturize-setup-card { background: #fff; border: 1px solid grey; overflow: hidden; }
        .auth-sidebar { background: #001226; color: white; }
        .step-item { display: flex; align-items: center; gap: 15px; opacity: 0.4; margin-bottom: 20px; }
        .step-item.active { opacity: 1; }
        .step-icon { width: 40px; height: 40px; border-radius: 12px; border: 2px solid #334155; display: flex; align-items: center; justify-content: center; }
        .auth-label { font-size: 11px; font-weight: 700; color: #94a3b8; margin-bottom: 6px; display: block; text-transform: uppercase; }
        .auth-field { width: 100%; padding: 12px; border: 1.5px solid #e2e8f0; border-radius: 12px; background: #f8fafc; }
        .btn-setup-primary { background: #0078d4; color: white; border: none; padding: 12px 24px; border-radius: 12px; font-weight: 700; }
        .btn-setup-finish { background: #10b981; color: white; border: none; padding: 12px 30px; border-radius: 12px; font-weight: 700; }
        .btn-setup-secondary { background: #f1f5f9; color: #475569; border: none; padding: 12px 24px; border-radius: 12px; }
        .section-title { border-bottom: 1px solid #f1f5f9; padding-bottom: 10px; font-weight: 700; display: flex; align-items: center; }
        .setup-progress-container { width: 100%; height: 6px; background: #f1f5f9; border-radius: 10px; overflow: hidden; }
        .progress-fill { height: 100%; background: #0078d4; transition: width 0.4s; }
      `}</style>
    </div>
  );
};

export default Cadastro;